import { trpc } from '@/config/trpc'
import { type PublishablePlatformType } from '@backend/lib/db/schema/platform'
import { getPlatformName } from './workspace-preview-validation'

export type SyncIssueKind = 'missing_scopes' | 'needs_reauth' | 'rate_limited' | 'error'

export type PlatformSyncIssue = {
  platform: PublishablePlatformType
  kind: SyncIssueKind
}

/**
 * Returns per-platform sync issues drawn from `platform.isAuthenticated`.
 *
 * `syncStatus` is written by the 6-hourly analytics cron. A warning here is
 * authoritative (the last sync run confirmed the issue), but the *absence*
 * of a warning is not a guarantee — a token revoked since the last sync
 * still shows healthy here. The postFailed email is the safety net for the
 * staleness gap; this hook is the early-warning surface.
 */
export function usePlatformSyncIssues(): PlatformSyncIssue[] {
  const linkedin = trpc.platform.isAuthenticated.useQuery({ platform: 'linkedin' })
  const instagram = trpc.platform.isAuthenticated.useQuery({ platform: 'instagram' })
  const tiktok = trpc.platform.isAuthenticated.useQuery({ platform: 'tiktok' })
  const youtube = trpc.platform.isAuthenticated.useQuery({ platform: 'youtube' })
  const facebook = trpc.platform.isAuthenticated.useQuery({ platform: 'facebook' })

  const queries = [
    ['linkedin', linkedin],
    ['instagram', instagram],
    ['tiktok', tiktok],
    ['youtube', youtube],
    ['facebook', facebook],
  ] as const

  const result: PlatformSyncIssue[] = []
  for (const [platform, q] of queries) {
    if (!q.data || !q.data.isAuthenticated) continue
    // syncStatus is server-managed truth — written by the analytics cron and
    // by the interactive `platform.post` mutation on auth-class failures.
    // Prefer it when set, since a `needs_reauth` write should win over the
    // derived `missing_scopes` signal (a token revocation matters more for
    // UX than a missing analytics scope).
    if (q.data.syncStatus && q.data.syncStatus !== 'ok') {
      result.push({ platform, kind: q.data.syncStatus })
      continue
    }
    // Fall back to the derived scope check only when no sync run has
    // populated syncStatus yet — this surfaces missing analytics scopes
    // immediately after OAuth, before the cron has run.
    if (q.data.analyticsAvailable === false) {
      result.push({ platform, kind: 'missing_scopes' })
    }
  }
  return result
}

export const formatPlatformList = (platforms: PublishablePlatformType[]): string =>
  platforms.map(getPlatformName).reduce((acc, name, i, arr) => {
    if (i === 0) return name
    if (i === arr.length - 1) return `${acc} and ${name}`
    return `${acc}, ${name}`
  }, '')
